import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { MODULE_CHAINE_CINEMATIQUE as M } from '../data/modulesConduite';
import useMetaTags from '../lib/useMetaTags';
import SceneInteractiveSVG from '../components/SceneInteractiveSVG';
import { SceneBoiteVitesses, SvgSpritesDefs } from '../components/SvgScenes';
import {
  Cog, Play, ArrowLeft, ArrowRight, Info, AlertTriangle, CheckCircle2, XCircle,
  Gauge, Sparkles, Youtube, Target, RotateCcw, ChevronRight,
} from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// SVG interactif : la chaîne cinématique complète (6 organes cliquables)
// ══════════════════════════════════════════════════════════════════════════

function ChaineCinematiqueSVG({ selected, onSelect }) {
  // Positions relatives des 6 organes le long de la chaîne (moteur → roues)
  const organes = [
    { id: 'moteur',       x: 90,  y: 90, label: 'Moteur' },
    { id: 'embrayage',    x: 210, y: 90, label: 'Embrayage' },
    { id: 'boite',        x: 330, y: 90, label: 'Boîte' },
    { id: 'arbre',        x: 460, y: 90, label: 'Arbre' },
    { id: 'differentiel', x: 590, y: 90, label: 'Différentiel' },
    { id: 'roues',        x: 720, y: 90, label: 'Roues motrices' },
  ];

  return (
    <svg viewBox="0 0 810 200" className="w-full h-auto" role="img" aria-label="Schéma interactif de la chaîne cinématique">
      {/* Ligne de fond reliant les organes */}
      <line x1={90} y1={90} x2={720} y2={90} stroke="#7f1d1d" strokeWidth={4} strokeLinecap="round" opacity={0.5} />
      {/* Flèche indiquant le sens de la puissance */}
      <text x={405} y={40} textAnchor="middle" fill="#f87171" fontSize={11} fontFamily="monospace">← SENS DE LA PUISSANCE →</text>

      {organes.map((o, idx) => {
        const isSelected = selected === o.id;
        return (
          <g
            key={o.id}
            className="cursor-pointer focus:outline-none"
            onClick={() => onSelect(o.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(o.id); } }}
            tabIndex={0}
            role="button"
            aria-label={`Sélectionner ${o.label}`}
            aria-pressed={isSelected}
            data-testid={`svg-organe-${o.id}`}
          >
            {/* Cercle du composant */}
            <circle
              cx={o.x} cy={o.y} r={isSelected ? 34 : 28}
              fill={isSelected ? '#dc2626' : '#1f1f22'}
              stroke={isSelected ? '#fca5a5' : '#dc2626'}
              strokeWidth={isSelected ? 3 : 2}
              className="transition-all"
            />
            {/* Numéro d'ordre */}
            <text x={o.x} y={o.y + 4} textAnchor="middle" fill={isSelected ? '#fff' : '#f87171'} fontSize={16} fontWeight="bold">
              {idx + 1}
            </text>
            {/* Label sous le cercle */}
            <text x={o.x} y={o.y + 60} textAnchor="middle" fill={isSelected ? '#fca5a5' : '#e5e5e5'} fontSize={11} fontWeight={isSelected ? 'bold' : 'normal'}>
              {o.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function BlocSchema() {
  const [selected, setSelected] = useState('moteur');
  const organe = M.schema.organes.find((o) => o.id === selected);

  return (
    <section className="mt-10 rounded-2xl border border-red-500/25 bg-gradient-to-br from-red-950/15 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-8" data-testid="bloc-schema">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
        <Sparkles size={11} /> {M.schema.kicker}
      </div>
      <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">{M.schema.titre}</h2>
      <p className="mt-2 text-white/60 text-[13.5px]">{M.schema.consigne}</p>

      <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-4 sm:p-6 overflow-x-auto">
        <ChaineCinematiqueSVG selected={selected} onSelect={setSelected} />
      </div>

      {organe && (
        <div className="mt-6 rounded-xl border border-red-500/40 bg-red-950/20 p-5" data-testid={`detail-organe-${organe.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold">Organe sélectionné</div>
              <h3 className="mt-1 font-serif-editorial text-white text-[22px] leading-tight">{organe.nom}</h3>
              <p className="mt-0.5 text-red-200 text-[13px] italic">{organe.role}</p>
            </div>
            <div className="text-white/40 text-[11px] font-mono-spec">Clique un autre cercle pour changer</div>
          </div>
          <p className="mt-4 text-white/80 text-[14px] leading-relaxed">{organe.detail}</p>
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-950/20 p-3.5 flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-amber-300 mt-0.5 shrink-0" />
            <div className="text-amber-100 text-[13px] leading-relaxed"><span className="font-semibold text-amber-200">Piège : </span>{organe.pieges}</div>
          </div>
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Simulateur "Point de patinage" — slider vertical de la pédale d'embrayage
// ══════════════════════════════════════════════════════════════════════════

function BlocPatinage() {
  const [pos, setPos] = useState(100); // 0 = relâchée, 100 = enfoncée

  const etat = useMemo(() => {
    // Seuils alignés sur les positions déclarées dans la data (100/60/40/20/0).
    // On centre chaque plage autour de sa valeur cible pour que le slider tombe
    // sur l'étape attendue quand l'utilisateur choisit exactement cette position.
    if (pos > 80) return M.patinage.etapes[0]; // pédale enfoncée (100)
    if (pos > 50) return M.patinage.etapes[1]; // remontée lente (60)
    if (pos > 30) return M.patinage.etapes[2]; // POINT DE PATINAGE (40)
    if (pos > 10) return M.patinage.etapes[3]; // accélération (20)
    return M.patinage.etapes[4]; // pédale relâchée (0)
  }, [pos]);

  const isPatinage = etat.etat === 'patinage';
  const isEngaged = etat.etat === 'engaged';

  return (
    <section className="mt-10 rounded-2xl border border-red-500/25 bg-gradient-to-br from-red-950/15 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-8" data-testid="bloc-patinage">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
        <Target size={11} /> {M.patinage.kicker}
      </div>
      <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">{M.patinage.titre}</h2>
      <p className="mt-2 text-white/70 text-[14px] max-w-3xl leading-relaxed">{M.patinage.intro}</p>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {/* Slider vertical simulant la pédale */}
        <div className="rounded-xl border border-white/10 bg-black/40 p-5 flex flex-col items-center">
          <div className="text-white/50 text-[11.5px] font-mono-spec uppercase tracking-wider mb-3">Pédale d&apos;embrayage — position</div>
          <div className="relative h-64 w-24 flex items-center justify-center">
            {/* Piste verticale */}
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-2 rounded-full bg-white/10" />
            {/* Zones colorées */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-[45%] bg-white/5 rounded-t-full" />
            <div className="absolute top-[45%] left-1/2 -translate-x-1/2 w-2 h-[25%] bg-red-500/40" />
            <div className="absolute top-[70%] left-1/2 -translate-x-1/2 w-2 h-[30%] bg-emerald-500/30 rounded-b-full" />
            <input
              type="range"
              min={0} max={100}
              value={pos}
              onChange={(e) => setPos(Number(e.target.value))}
              data-testid="patinage-pedal-slider"
              className="absolute inset-0 w-64 h-8 opacity-0 cursor-pointer"
              style={{ transform: 'rotate(-90deg) translate(0, -50%)', transformOrigin: 'left top', top: '50%', left: '50%' }}
              aria-label="Position de la pédale d'embrayage"
            />
            {/* Curseur visuel */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-10 h-4 bg-red-500 rounded-full shadow-lg shadow-red-900/50 border-2 border-white/20 pointer-events-none transition-all"
              style={{ top: `${pos}%`, marginTop: '-8px' }}
            />
            <div className="absolute -left-6 top-0 text-[10px] text-white/40 font-mono-spec">Enfoncée</div>
            <div className="absolute -left-4 bottom-0 text-[10px] text-white/40 font-mono-spec">Relâchée</div>
          </div>
          {/* Slider horizontal accessible (secours) */}
          <input
            type="range" min={0} max={100} value={pos}
            onChange={(e) => setPos(Number(e.target.value))}
            className="mt-4 w-full accent-red-500"
            aria-label="Position pédale (contrôle secondaire)"
            data-testid="patinage-pedal-slider-h"
          />
          <div className="mt-3 flex items-center gap-2 text-[11px] font-mono-spec">
            <span className={`px-2 py-1 rounded-full ${isEngaged ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/5 text-white/40'}`}>Embrayé</span>
            <span className={`px-2 py-1 rounded-full ${isPatinage ? 'bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse' : 'bg-white/5 text-white/40'}`}>Patinage</span>
            <span className={`px-2 py-1 rounded-full ${etat.etat === 'debraye' ? 'bg-white/10 text-white/70 border border-white/20' : 'bg-white/5 text-white/40'}`}>Débrayé</span>
          </div>
        </div>

        {/* Panneau explicatif dynamique */}
        <div className="rounded-xl border border-red-500/40 bg-red-950/15 p-5" data-testid="patinage-explanation">
          <div className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-wider font-bold">Ce qui se passe</div>
          <h3 className="mt-1 font-serif-editorial text-white text-[20px] leading-tight">{etat.label}</h3>
          <p className="mt-3 text-white/80 text-[13.5px] leading-relaxed">{etat.desc}</p>
          {isPatinage && (
            <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-3 text-[12.5px] text-emerald-100 leading-relaxed">
              <CheckCircle2 size={14} className="inline text-emerald-300 mr-1.5" />
              Bravo — tu viens de trouver <span className="font-bold text-emerald-200">le point de patinage</span>. C&apos;est ce moment précis qu&apos;il faut ancrer dans les jambes.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="text-white/50 text-[11px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">💡 Astuce</div>
          <p className="text-white/80 text-[13px] leading-relaxed">{M.patinage.astuce}</p>
        </div>
        <div className="rounded-xl border border-red-500/30 bg-red-950/15 p-4">
          <div className="text-red-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">⚠️ Erreur typique</div>
          <p className="text-white/80 text-[13px] leading-relaxed">{M.patinage.erreur_typique}</p>
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Bloc "Choisir le bon rapport" — slider de vitesse qui highlight le rapport
// ══════════════════════════════════════════════════════════════════════════

function BlocRapport() {
  const [vitesse, setVitesse] = useState(30);

  // Détecter le rapport optimal — plages disjointes garanties dans la data,
  // mais on choisit par proximité au centre pour rester robuste si la data évolue.
  const rapportOptimal = useMemo(() => {
    const inRange = M.rapport.rapports.filter((r) => vitesse >= r.plage[0] && vitesse <= r.plage[1]);
    if (inRange.length === 0) return null;
    if (inRange.length === 1) return inRange[0];
    // Départage : rapport dont le centre de plage est le plus proche
    return inRange.reduce((best, r) => {
      const center = (r.plage[0] + r.plage[1]) / 2;
      const bestCenter = (best.plage[0] + best.plage[1]) / 2;
      return Math.abs(vitesse - center) < Math.abs(vitesse - bestCenter) ? r : best;
    });
  }, [vitesse]);

  return (
    <section className="mt-10 rounded-2xl border border-red-500/25 bg-gradient-to-br from-red-950/15 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-8" data-testid="bloc-rapport">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
        <Gauge size={11} /> {M.rapport.kicker}
      </div>
      <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">{M.rapport.titre}</h2>
      <p className="mt-2 text-white/70 text-[14px] max-w-3xl leading-relaxed">{M.rapport.intro}</p>

      <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-5 sm:p-7">
        <div className="flex items-center justify-between mb-3">
          <div className="text-white/50 text-[11px] font-mono-spec uppercase tracking-wider">Vitesse actuelle</div>
          <div className="font-mono-spec text-white text-[28px] font-bold" data-testid="rapport-vitesse-value">{vitesse} <span className="text-white/40 text-[14px]">km/h</span></div>
        </div>
        <input
          type="range" min={0} max={130} value={vitesse}
          onChange={(e) => setVitesse(Number(e.target.value))}
          className="w-full accent-red-500 h-2"
          data-testid="rapport-vitesse-slider"
          aria-label="Vitesse en km/h"
        />
        <div className="mt-1 flex justify-between text-[10px] text-white/30 font-mono-spec">
          <span>0</span><span>30</span><span>60</span><span>90</span><span>130 km/h</span>
        </div>

        {/* Rapports affichés en ligne */}
        <div className="mt-6 grid grid-cols-5 gap-2 sm:gap-3">
          {M.rapport.rapports.map((r) => {
            const isOptimal = rapportOptimal && rapportOptimal.rapport === r.rapport;
            const isInRange = vitesse >= r.plage[0] && vitesse <= r.plage[1];
            return (
              <div
                key={r.rapport}
                data-testid={`rapport-badge-${r.rapport}`}
                className={`rounded-lg border p-2.5 sm:p-3 text-center transition-all ${
                  isOptimal
                    ? 'border-emerald-400 bg-emerald-950/40 shadow-lg shadow-emerald-900/40 scale-105'
                    : isInRange
                    ? 'border-white/20 bg-white/[0.03]'
                    : 'border-white/10 bg-white/[0.01] opacity-40'
                }`}
              >
                <div className={`font-serif-editorial text-[22px] sm:text-[26px] leading-none ${isOptimal ? 'text-emerald-300' : 'text-white/80'}`}>{r.rapport}<span className="text-[13px]">ème</span></div>
                <div className="mt-1 text-[10px] text-white/50 font-mono-spec">{r.plage[0]}–{r.plage[1]}</div>
              </div>
            );
          })}
        </div>

        {rapportOptimal ? (
          <div className="mt-5 rounded-xl border border-emerald-500/40 bg-emerald-950/25 p-4" data-testid="rapport-optimal-info">
            <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Rapport recommandé</div>
            <div className="mt-1 font-serif-editorial text-white text-[18px]">{rapportOptimal.rapport}<span className="text-[13px]">ème</span> vitesse — {rapportOptimal.usage}</div>
            <div className="mt-1 text-white/60 text-[12px] font-mono-spec">Régime moteur cible : {rapportOptimal.regime}</div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-950/20 p-4">
            <div className="text-amber-300 text-[11px] font-mono-spec">À {vitesse} km/h tu es en zone tampon — deux rapports voisins sont possibles selon relief et accélération souhaitée.</div>
          </div>
        )}
      </div>

      {/* Signaux sur-régime / sous-régime */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-red-500/40 bg-red-950/20 p-4" data-testid="signal-sur-regime">
          <div className="text-red-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">🔊 {M.rapport.signaux.sur_regime.titre}</div>
          <ul className="mt-2 space-y-1 text-white/75 text-[13px]">
            {M.rapport.signaux.sur_regime.symptomes.map((s, i) => <li key={i} className="flex items-start gap-1.5"><ChevronRight size={13} className="text-red-400 mt-0.5 shrink-0" />{s}</li>)}
          </ul>
          <div className="mt-2 pt-2 border-t border-red-500/20 text-emerald-300 text-[12.5px]">→ {M.rapport.signaux.sur_regime.solution}</div>
        </div>
        <div className="rounded-xl border border-amber-500/40 bg-amber-950/20 p-4" data-testid="signal-sous-regime">
          <div className="text-amber-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">🐌 {M.rapport.signaux.sous_regime.titre}</div>
          <ul className="mt-2 space-y-1 text-white/75 text-[13px]">
            {M.rapport.signaux.sous_regime.symptomes.map((s, i) => <li key={i} className="flex items-start gap-1.5"><ChevronRight size={13} className="text-amber-400 mt-0.5 shrink-0" />{s}</li>)}
          </ul>
          <div className="mt-2 pt-2 border-t border-amber-500/20 text-emerald-300 text-[12.5px]">→ {M.rapport.signaux.sous_regime.solution}</div>
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Bloc vidéos
// ══════════════════════════════════════════════════════════════════════════

function BlocVideos() {
  return (
    <section className="mt-10" data-testid="bloc-videos">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-950/20 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
        <Youtube size={11} /> Bloc 4 · Vidéos en appui
      </div>
      <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">À projeter en salle ou en voiture</h2>
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        {M.videos.map((v) => (
          <div key={v.id} className="rounded-xl border border-white/10 bg-black/40 overflow-hidden" data-testid={`video-${v.id}`}>
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${v.id}`}
                title={v.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            <div className="p-4">
              <div className="font-serif-editorial text-white text-[16px] leading-tight">{v.title}</div>
              <div className="mt-1 text-white/50 text-[12px] font-mono-spec">{v.note}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Bloc études de cas
// ══════════════════════════════════════════════════════════════════════════

function CasFeuVert({ cas }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-950/15 p-5 sm:p-6" data-testid={`cas-${cas.id}`}>
      <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Étude de cas 1</div>
      <h3 className="mt-1 font-serif-editorial text-white text-[22px] leading-tight">{cas.titre}</h3>
      <p className="mt-3 text-white/75 text-[14px] leading-relaxed">{cas.contexte}</p>

      <button
        onClick={() => setReveal(!reveal)}
        data-testid={`cas-reveal-${cas.id}`}
        className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-red-500/60 bg-red-500/15 text-red-100 hover:bg-red-500/25 text-[13px] font-bold transition"
      >
        {reveal ? 'Masquer la solution' : 'Voir la séquence correcte'} <ArrowRight size={13} />
      </button>

      {reveal && (
        <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-4" data-testid={`cas-solution-${cas.id}`}>
          <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2">✓ Séquence correcte</div>
          <ol className="space-y-1.5 text-white/85 text-[13.5px]">
            {cas.etapes_correctes.map((e, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
                <span>{e}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4 pt-3 border-t border-emerald-500/20 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div className="text-amber-100 text-[12.5px] leading-relaxed"><span className="font-semibold">Piège fréquent : </span>{cas.pieges}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CasDescente({ cas }) {
  const [choix, setChoix] = useState(null);
  // Options mélangées une seule fois (pas de re-shuffle à chaque render)
  const shuffled = useMemo(() => {
    const opts = [
      { text: cas.bonne_reponse, correct: true },
      ...cas.alternatives_fausses.map((t) => ({ text: t, correct: false })),
    ];
    // Shuffle simple mais stable via useMemo
    for (let i = opts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [opts[i], opts[j]] = [opts[j], opts[i]];
    }
    return opts;
  }, [cas]);

  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-950/15 p-5 sm:p-6" data-testid={`cas-${cas.id}`}>
      <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Étude de cas 2</div>
      <h3 className="mt-1 font-serif-editorial text-white text-[22px] leading-tight">{cas.titre}</h3>
      <p className="mt-3 text-white/75 text-[14px] leading-relaxed">{cas.contexte}</p>

      <div className="mt-4 space-y-2">
        {shuffled.map((opt, i) => {
          const isSelected = choix === i;
          const showResult = choix !== null;
          const isCorrect = opt.correct;
          return (
            <button
              key={i}
              onClick={() => choix === null && setChoix(i)}
              disabled={choix !== null}
              data-testid={`cas-${cas.id}-option-${i}`}
              className={`w-full text-left p-3.5 rounded-lg border transition text-[13.5px] flex items-start gap-2.5 ${
                showResult
                  ? isCorrect
                    ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-100'
                    : isSelected
                    ? 'border-red-500/60 bg-red-950/40 text-red-100'
                    : 'border-white/10 bg-white/[0.02] text-white/40'
                  : 'border-white/15 bg-white/[0.03] text-white/85 hover:border-red-500/40 hover:bg-red-950/15'
              }`}
            >
              {showResult && isCorrect && <CheckCircle2 size={16} className="text-emerald-300 mt-0.5 shrink-0" />}
              {showResult && !isCorrect && isSelected && <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />}
              {!showResult && <div className="w-4 h-4 rounded-full border border-white/30 mt-0.5 shrink-0" />}
              <span className="leading-relaxed">{opt.text}</span>
            </button>
          );
        })}
      </div>

      {choix !== null && (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-black/30 p-4" data-testid={`cas-${cas.id}-explanation`}>
          <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Explication</div>
          <p className="mt-1.5 text-white/85 text-[13.5px] leading-relaxed">{cas.explication}</p>
          <button
            onClick={() => setChoix(null)}
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-red-300 hover:text-red-200"
          >
            <RotateCcw size={12} /> Recommencer
          </button>
        </div>
      )}
    </div>
  );
}

function BlocCas() {
  return (
    <section className="mt-10" data-testid="bloc-cas">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-950/20 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
        <Info size={11} /> Bloc 5 · Études de cas
      </div>
      <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">Applique en situation réelle</h2>
      <div className="mt-5 grid gap-5">
        {M.cas.map((c) => (
          c.id === 'feu-vert' ? <CasFeuVert key={c.id} cas={c} /> : <CasDescente key={c.id} cas={c} />
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Bloc QCM d'ancrage
// ══════════════════════════════════════════════════════════════════════════

function BlocQuiz() {
  const [answers, setAnswers] = useState({}); // {qId: optionIdx}
  const [submitted, setSubmitted] = useState(false);

  const score = useMemo(() => {
    if (!submitted) return null;
    return M.quiz.filter((q) => answers[q.id] === q.correct).length;
  }, [submitted, answers]);

  const reset = () => { setAnswers({}); setSubmitted(false); };

  return (
    <section className="mt-10 rounded-2xl border border-red-500/25 bg-gradient-to-br from-red-950/15 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-8" data-testid="bloc-quiz">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
        <CheckCircle2 size={11} /> Bloc 6 · Quiz d&apos;ancrage
      </div>
      <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">Vérifie ce que tu as retenu</h2>

      <div className="mt-6 space-y-6">
        {M.quiz.map((q, qi) => (
          <div key={q.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`quiz-q-${qi}`}>
            <div className="text-white/50 text-[11px] font-mono-spec">Question {qi + 1} / {M.quiz.length}</div>
            <div className="mt-1 font-serif-editorial text-white text-[17px] leading-tight">{q.question}</div>
            <div className="mt-4 space-y-2">
              {q.options.map((opt, oi) => {
                const selected = answers[q.id] === oi;
                const isCorrect = submitted && oi === q.correct;
                const isWrongSelected = submitted && selected && oi !== q.correct;
                return (
                  <button
                    key={oi}
                    onClick={() => !submitted && setAnswers({ ...answers, [q.id]: oi })}
                    disabled={submitted}
                    data-testid={`quiz-q-${qi}-opt-${oi}`}
                    className={`w-full text-left p-3 rounded-lg border transition text-[13.5px] flex items-start gap-2.5 ${
                      isCorrect
                        ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-100'
                        : isWrongSelected
                        ? 'border-red-500/60 bg-red-950/40 text-red-100'
                        : selected
                        ? 'border-red-400/70 bg-red-500/10 text-white'
                        : 'border-white/15 bg-white/[0.02] text-white/80 hover:border-red-500/40'
                    }`}
                  >
                    {isCorrect && <CheckCircle2 size={16} className="text-emerald-300 mt-0.5 shrink-0" />}
                    {isWrongSelected && <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />}
                    {!submitted && (
                      <div className={`w-4 h-4 rounded-full border mt-0.5 shrink-0 ${selected ? 'bg-red-500 border-red-400' : 'border-white/30'}`} />
                    )}
                    <span className="leading-relaxed">{opt}</span>
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className="mt-3 rounded-lg border border-red-500/30 bg-black/30 p-3 text-[12.5px] text-white/80 leading-relaxed" data-testid={`quiz-q-${qi}-explanation`}>
                <span className="text-red-300 font-mono-spec text-[10.5px] uppercase font-bold">Explication : </span>{q.explanation}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 justify-between">
        {!submitted ? (
          <button
            onClick={() => setSubmitted(true)}
            disabled={Object.keys(answers).length < M.quiz.length}
            data-testid="quiz-submit"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14px] font-bold transition shadow-lg shadow-red-900/40"
          >
            Valider mes réponses <ArrowRight size={14} />
          </button>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-serif-editorial text-[18px] ${
              score === M.quiz.length ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-200' : score >= 3 ? 'border-amber-500/60 bg-amber-950/30 text-amber-200' : 'border-red-500/60 bg-red-950/40 text-red-200'
            }`} data-testid="quiz-score">
              Score : {score} / {M.quiz.length}
            </div>
            <button
              onClick={reset}
              data-testid="quiz-reset"
              className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border border-white/20 text-white/85 hover:bg-white/5 text-[13px] font-semibold"
            >
              <RotateCcw size={13} /> Refaire le quiz
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Page principale
// ══════════════════════════════════════════════════════════════════════════

export default function ModuleChaineCinematiquePage() {
  useMetaTags({
    title: `${M.title} — Module interactif — Code Voiture`,
    description: M.intro.slice(0, 200),
    ogUrl: `https://codevoiture.fr/modules-conduite/${M.slug}`,
    ogType: 'article',
  });

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 py-10 sm:py-14">
        {/* Fil d'ariane */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6 flex-wrap">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/modules-conduite" className="hover:text-white">Modules interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Chaîne cinématique</span>
        </nav>

        <Link to="/modules-conduite" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[13px] mb-6" data-testid="module-back-link">
          <ArrowLeft size={14} /> Tous les modules
        </Link>

        {/* En-tête */}
        <div className="rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-950/25 via-[#0d0d0e] to-[#0a0a0b] p-6 sm:p-9 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 opacity-[0.08] pointer-events-none">
            <Cog size={220} strokeWidth={1} />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <Cog size={11} /> Module M1 · Chaîne cinématique
            </div>
            <h1 className="mt-4 font-serif-editorial text-white text-[38px] sm:text-[48px] leading-[1.02] tracking-tight">{M.title}</h1>
            <p className="mt-4 text-white/75 text-[15px] sm:text-[16px] max-w-3xl leading-relaxed">{M.intro}</p>

            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2">Objectifs pédagogiques ECSR</div>
              <ul className="space-y-1.5">
                {M.objectifs_ecsr.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px]">
                    <CheckCircle2 size={13} className="text-red-400 mt-1 shrink-0" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Blocs */}
        <SvgSpritesDefs />
        <BlocSchema />
        <BlocPatinage />
        <BlocRapport />

        {/* NOUVEAU · Bloc "Manipule le levier" — SVG interactif boîte de vitesses */}
        <section className="mt-10" data-testid="bloc-boite">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Sparkles size={11} /> Bloc bonus · Manipule le levier
          </div>
          <h2 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[32px] leading-tight">Le H de vitesses en pratique</h2>
          <p className="mt-2 text-white/70 text-[14px] max-w-3xl leading-relaxed">
            Fais glisser le point rouge du levier sur le rapport que tu veux enclencher. Clique <span className="text-emerald-300">« Afficher trajet »</span> pour révéler la position de la <span className="text-emerald-300">3ème</span>.
          </p>
          <div className="mt-5">
            <SceneInteractiveSVG
              title="Cible la 3ème vitesse"
              hint="Le H est la disposition standard : R en haut à gauche (marche arrière), les impaires en haut (1, 3, 5) et les paires en bas (2, 4, 6). Pour aller de 2ème à 3ème : tu ramènes le levier au point mort (position centrale) puis tu pousses vers 3."
              toggleLabel="Afficher la 3ème"
              testid="scene-boite"
            >
              <SceneBoiteVitesses />
            </SceneInteractiveSVG>
          </div>
        </section>

        <BlocVideos />
        <BlocCas />
        <BlocQuiz />

        {/* CTA fin */}
        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center" data-testid="module-cta-end">
          <Sparkles size={28} className="mx-auto text-red-400 mb-3" />
          <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Prêt pour le prochain module ?</h3>
          <p className="mt-2 text-white/60 text-[13.5px] max-w-2xl mx-auto">La suite : contrôles & angles morts, indices formels/informels, intersections et giratoires — bientôt disponibles.</p>
          <Link to="/modules-conduite" className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold" data-testid="module-cta-back">
            Retour aux modules <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
